'use client';
import { FC, useCallback, useMemo, useState } from 'react';
import Contests from '@/widgets/Contests/Contests';
import Layout from '@/widgets/Layout/Layout';
import HeaderButtonsDefault from '@/widgets/Header/ui/HeaderButtons/HeaderButtonsDefault';
import Header from '@/widgets/Header/ui/Header';
import useQueryParam from '@/shared/hooks/useQueryParam';
import { useGetContests } from '@/shared/hooks/useGetContests';
import { cva } from 'class-variance-authority';
import { useLocale } from 'next-intl';
import {
  IContestDetail,
  TContestStatus,
} from '@/shared/api/contest/getContests/types';
import { ContestListTags } from '@/features/ContestListTags/ContestListTags';
import useMeasure from 'react-use-measure';

interface Props {}

export const ContestPageClient: FC<Props> = () => {
  const [headerRef, headerMeasure] = useMeasure();

  const locale = useLocale();

  const [category] = useQueryParam<'active' | 'completed' | undefined>(
    'category'
  );

  const { data, isFetching } = useGetContests(
    {
      lang: locale,
    },
    {
      status: category
        ? ({
            active: ['announce', 'active'],
            completed: ['completed'],
          }[category] as TContestStatus[])
        : undefined,
    }
  );

  const tags = useMemo(() => {
    return (
      data?.map((i) => ({
        id: i.id,
        title: i.title.rendered ?? '',
      })) ?? []
    );
  }, [data]);

  const [activeTagId, setActiveTagId] = useState(tags?.[0]?.id);

  const onContestViewportEnter = useCallback((contest: IContestDetail) => {
    setActiveTagId(contest.id);
  }, []);

  return (
    <Layout
      headerSlot={
        <Header
          ref={headerRef}
          buttonsSlot={<HeaderButtonsDefault />}
          afterSlot={<ContestListTags tags={tags} activeTagId={activeTagId} />}
        />
      }>
      <main className={cvaMain()}>
        <Contests
          contests={data ?? []}
          isLoading={isFetching}
          scrollMarginTop={headerMeasure.height}
          onContestViewportEnter={onContestViewportEnter}
        />
      </main>
    </Layout>
  );
};

const cvaMain = cva([
  'ContestPageClient-cvaMain',
  'mt-6 mb-4.2 md-max:mt-4 md-max:mb-3.2',
]);
